import { useEffect, useState, useRef } from "react";
import { getLigaTV } from "../../../services/getLigaTV";
import CardPartido from "./cardPartido/cardPartido";
import Loading from "../../loading/loading";
import marker from "../../../assets/w-tv/marker.png";
import logoDefault from "../../../assets/escudo-default-foot.png";
import "./ligaTV.sass";
import TickerClarosportsPage from "../../../pages/tickerClarosports";


const GAMES_PER_GROUP = 7;
const GROUP_ROTATION_TIME = 20000; // 20 segundos

export default function LigaTV({ idligas }) {
  const [ligas, setLigas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [visibleGroups, setVisibleGroups] = useState({}); // Controla qué grupo visible en cada liga
  const [isGroupTransitioning, setIsGroupTransitioning] = useState({}); // Estado de transición por liga
  const [animationTrigger, setAnimationTrigger] = useState(0); // Trigger para reiniciar animaciones
  
  const refreshIntervalRef = useRef(null);
  const groupRotationIntervalsRef = useRef({}); // Intervalo de rotación para cada liga
  const transitionTimeoutRef = useRef({}); // Timeout para limpiar transición
  const hasLoadedRef = useRef(false);

  // Función para cargar las ligas
  const fetchLigasData = async (showLoading = true) => {
    if (!idligas) {
      setError("No se proporcionaron IDs de ligas");
      if (showLoading) setLoading(false);
      return;
    }

    try {
      if (showLoading) setLoading(true);
      else setIsRefreshing(true);

      const { data, statusCode } = await getLigaTV(idligas);

      if (statusCode === 200) {
        // Filtrar ligas que tengan eventos
        const ligasConEventos = (Array.isArray(data) ? data : [data]).filter(
          (liga) => liga.events && liga.events.length > 0
        );
        setLigas(ligasConEventos);
        setError(null);

        // Trigger animación en refresh
        setAnimationTrigger((prev) => prev + 1);

        // Inicializar visible groups para cada liga
        const newVisibleGroups = {};
        ligasConEventos.forEach((liga, index) => {
          newVisibleGroups[index] = 0; // Mostrar grupo 0 inicialmente
        });
        setVisibleGroups(newVisibleGroups);
      } else {
        setError(`Error: ${statusCode}`);
        setLigas([]);
      }
    } catch (err) {
      setError(err.message || "Error desconocido");
      setLigas([]);
    } finally {
      if (showLoading) setLoading(false);
      else setIsRefreshing(false);
    }
  };

  // Función para iniciar rotación de grupos para una liga
  const startGroupRotation = (ligaIndex, totalGames) => {
    // Limpiar intervalo anterior si existe
    if (groupRotationIntervalsRef.current[ligaIndex]) {
      clearInterval(groupRotationIntervalsRef.current[ligaIndex]);
    }

    // Solo configurar rotación si hay más de GAMES_PER_GROUP partidos
    if (totalGames > GAMES_PER_GROUP) {
      const totalGroups = Math.ceil(totalGames / GAMES_PER_GROUP);

      groupRotationIntervalsRef.current[ligaIndex] = setInterval(() => {
        // Activar transición
        setIsGroupTransitioning((prev) => ({
          ...prev,
          [ligaIndex]: true,
        }));

        // Cambiar el grupo
        setVisibleGroups((prev) => ({
          ...prev,
          [ligaIndex]: (prev[ligaIndex] + 1) % totalGroups,
        }));

        // Trigger animación en elementos
        setAnimationTrigger((prev) => prev + 1);

        // Limpiar transición después de 500ms (duración de la animación)
        if (transitionTimeoutRef.current[ligaIndex]) {
          clearTimeout(transitionTimeoutRef.current[ligaIndex]);
        }
        transitionTimeoutRef.current[ligaIndex] = setTimeout(() => {
          setIsGroupTransitioning((prev) => ({
            ...prev,
            [ligaIndex]: false,
          }));
        }, 400);
      }, GROUP_ROTATION_TIME);
    }
  };

  // Cargar datos iniciales y configurar refresh automático
  useEffect(() => {
    // Evitar ejecución duplicada
    if (hasLoadedRef.current) {
      return;
    }

    const loadInitialData = async () => {
      hasLoadedRef.current = true;

      // Cargar datos iniciales (con loading)
      await fetchLigasData(true);

      // Configurar actualización automática cada 60 segundos (60000 ms)
      refreshIntervalRef.current = setInterval(async () => {
        try {
          await fetchLigasData(false);
        } catch (err) {
          console.error("Error en el refresh automático de ligas:", err);
        }
      }, 60000);
    };

    loadInitialData();

    // Cleanup al desmontar el componente
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      // Limpiar todos los intervalos de rotación
      Object.values(groupRotationIntervalsRef.current).forEach((interval) => {
        if (interval) clearInterval(interval);
      });
      // Limpiar todos los timeouts de transición
      Object.values(transitionTimeoutRef.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [idligas]);

  // Configurar rotación de grupos cuando cambien las ligas
  useEffect(() => {
    // Solo activar agrupación cuando se visualizan 2 ligas
    if (ligas.length === 2) {
      ligas.forEach((liga, index) => {
        const totalGames = liga.events?.length || 0;
        startGroupRotation(index, totalGames);
      });
    } else {
      // Limpiar intervalos si no estamos en vista de 2 ligas
      Object.values(groupRotationIntervalsRef.current).forEach((interval) => {
        if (interval) clearInterval(interval);
      });
      groupRotationIntervalsRef.current = {};
    }

    return () => {
      // Limpiar intervalos al desmontar
      Object.values(groupRotationIntervalsRef.current).forEach((interval) => {
        if (interval) clearInterval(interval);
      });
    };
  }, [ligas]);

  // Función para obtener los partidos visibles de una liga
  const getVisibleGames = (liga, ligaIndex) => {
    // Si es una sola liga, mostrar todos los partidos sin agrupar
    if (ligas.length === 1) {
      return liga.events || [];
    }

    // Si son 2 ligas, aplicar agrupación
    const totalGames = liga.events?.length || 0;
    const currentGroup = visibleGroups[ligaIndex] || 0;
    const startIndex = currentGroup * GAMES_PER_GROUP;
    const endIndex = startIndex + GAMES_PER_GROUP;

    return liga.events.slice(startIndex, endIndex);
  };

  // Función para obtener información del grupo actual
  const getGroupInfo = (liga, ligaIndex) => {
    // No mostrar información de grupo si es solo 1 liga
    if (ligas.length === 1) {
      return null;
    }

    const totalGames = liga.events?.length || 0;
    if (totalGames <= GAMES_PER_GROUP) return null;

    const totalGroups = Math.ceil(totalGames / GAMES_PER_GROUP);
    const currentGroup = visibleGroups[ligaIndex] || 0;

    return {
      currentGroup: currentGroup + 1,
      totalGroups,
      startGame: currentGroup * GAMES_PER_GROUP + 1,
      endGame: Math.min((currentGroup + 1) * GAMES_PER_GROUP, totalGames),
      totalGames,
    };
  };

  if (loading) return <div className="liga-tv-container loading" type={"1080px"}><Loading /></div>;
  if (error) return <div className="liga-tv-container error">Error: {error}</div>;

  // Determinar el layout basado en cantidad de ligas
  const layoutClass = ligas.length === 1 ? "layout-single" : "layout-double";

  return (
    <div className={`liga-tv-container ${isRefreshing ? 'refreshing' : ''}`}>
      {ligas && ligas.length > 0 ? (
        <div className={`ligas-list ${layoutClass}`}>
          {ligas.map((liga, ligaIndex) => {
            const visibleGames = getVisibleGames(liga, ligaIndex);
            const groupInfo = getGroupInfo(liga, ligaIndex);

            return (
              <div key={`liga-${ligaIndex}`} className="liga-section">
                <div className="liga-header">
                  <div className="box-header">
                    <img className="liga-img" src={liga.league_image ? liga.league_image : logoDefault}></img>
                    <h2 className="liga-name">{liga.league_name}</h2>
                    {groupInfo && (
                      <div className="group-info">
                        <span className="group-counter">
                        {groupInfo.currentGroup} de {groupInfo.totalGroups}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`games-list ${isGroupTransitioning[ligaIndex] ? 'transitioning' : ''}`}>
                  {visibleGames.map((game, gameIndex) => (
                    <CardPartido
                      key={`${game.id_juego}-${animationTrigger}`}
                      game={game}
                      gameIndex={gameIndex}
                      animationTrigger={animationTrigger}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="no-ligas">No se encontraron ligas con partidos</div>
      )}
        <div className="item-qr">
          <img src={marker} alt="marker" />
        </div>

        <TickerClarosportsPage />
    </div>
  );
}